import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton, Empty, Image, Modal } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import CustomPagination from '../../components/common/CustomPagination';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import httpClient from '../../utils/HttpClient';
import { GALLERY_URL } from '../../constants/Url';
import NotFoundPage from '../NotFoundPage';
import AuthModal from '../../components/auth/AuthModal';
import FashionModals from '../../components/FashionModals';
import PROModal from '../../components/PROModal';
import { useMemberAccess } from '../../hooks/useMemberAccess';

export default function GalleryDetailPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const authModalRef = useRef();
    const appDownloadRef = useRef();
    const user = useSelector((state) => state.authReducer);
    let token = user && user?.token ? user?.token : null;
    const { hasNoActiveMembership } = useMemberAccess();
    const [isMemberGateOpen, setIsMemberGateOpen] = useState(false);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [activeModalData, setActiveModalData] = useState(null);

    // Image Preview States
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewData, setPreviewData] = useState(null)

    // Pagination & Tabs states
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState('男');
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(9);

    const fetchDetail = async () => {
        try {
            setLoading(true);
            const res = await httpClient.get(`${GALLERY_URL}/${id}`)
                .then(res => res.data)
                .catch(error => ({ status: 500, message: error?.message }));

            if (res && res.status === 200 && res.data) {
                setData(res.data);
                setNotFound(false);
            } else {
                setNotFound(true);
            }
        } catch (error) {
            console.error('fetchDetail -> error', error);
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchCreativeList = async () => {
        try {
            setLoading(true);
            const res = await httpClient.get(`${GALLERY_URL}?pageNo=${currentPage}&pageSize=${pageSize}&category=${activeTab}`)
                .then(res => res.data)
                .catch(error => ({ status: 500, message: error?.message }));

            if (res && res.status === 200 && res.data) {
                setData(res.data);
                setTotal(res.total);
                setNotFound(false);
            } else {
                setData([]);
                setTotal(0);
                setNotFound(false);
            }
        } catch (error) {
            console.error('fetchCreativeList -> error', error);
            setData([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    const isListMode = !id || id === 'detail';

    useEffect(() => {
        if (id && id !== 'detail') {
            fetchDetail();
        } else {
            fetchCreativeList();
        }
    }, [id, activeTab, currentPage, pageSize]);

    useEffect(() => {
        if (id && id !== 'detail' && user?.token && hasNoActiveMembership()) {
            setIsMemberGateOpen(true);
        }
    }, [id, user?.token, hasNoActiveMembership]);

    const handleShowDownloadApp = (item) => {
        if (!item) return;
        setActiveModalData(item);
        if (!token) {
            authModalRef.current.open();
            return;
        }
        if (!user?.user?.completedTS) {
            authModalRef.current.open('REGISTER');
            return;
        }
        if (hasNoActiveMembership()) {
            setIsMemberGateOpen(true);
            return;
        }
        appDownloadRef.current?.showModal(item);
    };

    const handleImageView = (url, item) => {
        if (!token) {
            authModalRef.current.open();
            return;
        }
        if (hasNoActiveMembership()) {
            setIsMemberGateOpen(true);
            return;
        }
        if (url) {
            setPreviewImage(url);
            setPreviewData(item)
            setPreviewOpen(true);
        }
    };



    if (notFound || !data) {
        return <NotFoundPage />;
    }

    return (
        <div className="w-full min-h-screen pb-16 bg-[#f7f9fc]">
            <AuthModal ref={authModalRef} />

            <Modal
                open={previewOpen}
                onCancel={() => setPreviewOpen(false)}
                footer={null}
                centered
                width={600} // Give it a good width for images
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0' }}>
                    {previewImage && (
                        <img 
                            src={previewImage} 
                            alt="Preview" 
                            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '5px' }} 
                        />
                    )}
                    {(previewData?.description || (previewData?.tags && previewData.tags.length > 0)) && (
                        <div style={{
                            color: '#333',
                            marginTop: 20,
                            fontSize: 16,
                            fontWeight: 500,
                            lineHeight: 1.5,
                            width: '100%',
                        }}>
                            {previewData?.description && (
                                <div style={{ marginBottom: (previewData?.tags && previewData.tags.length > 0) ? '10px' : 0 }}>
                                    {previewData.description}
                                </div>
                            )}
                            {previewData?.tags?.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {previewData.tags.map((tag, index) => (
                                        <Button
                                            key={`${tag}-${index}`}
                                            type='text'
                                            size="small"
                                            className="tag-button !cursor-default !hover:bg-transparent"
                                            style={{ verticalAlign: 'baseline' }}
                                        >
                                            {tag}
                                        </Button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>

            {isListMode ? (
                /* Show All Data Mode - Matching Gallery Grid Design */
                <div className="w-full">
                    {/* Header Banner Section */}
                    <div className="w-full py-10 px-4 text-center bg-gradient-to-r from-[#4d9dec] via-[#637cfb] to-[#a380f9] text-white">
                        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide m-0 text-white mb-2">
                            {t('menu_creative')}
                        </h1>
                    </div>
                    <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-10">
                        {/* Header selection card / tabs bar */}
                        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center border-b border-gray-200 pb-3 mb-8">
                            <h2 className="text-[17px] sm:text-[20px] font-bold text-gray-900 tracking-wide m-0">
                                {t('menu_creative')}
                            </h2>
                            <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-8">

                                <div className="flex gap-4 sm:gap-6 text-[13px] sm:text-[14px] font-medium overflow-x-auto">
                                    {[
                                        {
                                            label: "text_male",
                                            value: "男"
                                        },
                                        {
                                            label: "text_female",
                                            value: "女"
                                        },
                                        {
                                            label: "text_creative",
                                            value: "前卫创意"
                                        },
                                    ].map((tab) => (
                                        <div
                                            key={tab.value}
                                            onClick={() => { 
                                                // if (!token) {
                                                //     authModalRef.current.open();
                                                //     return;
                                                // }
                                                setActiveTab(tab.value); setCurrentPage(1); setPageSize(9); 
                                            }}
                                            className={`cursor-pointer pb-2 relative transition-colors whitespace-nowrap ${activeTab === tab.value ? 'text-black font-semibold' : 'text-gray-400 hover:text-gray-600'
                                                }`}
                                        >
                                            {t(tab.label) || tab.value}
                                            {activeTab === tab.value && (
                                                <span className="absolute bottom-0 left-0 right-0 h-[3px] rounded-full" style={{ background: "linear-gradient(to right, #50c7f8, #7887fd, #df97fb)" }} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <div key={index} className="w-full aspect-[3/4] rounded-lg overflow-hidden bg-gray-100 relative">
                                        <Skeleton.Image active className="!w-full !h-full" style={{ width: '100%', height: '100%' }} />
                                    </div>
                                ))}
                            </div>
                        ) : Array.isArray(data) && data.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                                {data.map((item, index) => (
                                    <div
                                        key={item._id || index}
                                        className="group overflow-hidden rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow duration-300 w-full aspect-[3/4]"
                                        onClick={() => handleImageView(item.images?.[0]?.url, item)}
                                    >
                                        {item.images?.[0]?.url && (
                                            <img
                                                src={item.images?.[0]?.url}
                                                alt={item.galleryId || 'gallery-image'}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Empty description={t('text_no_work')} />
                        )}

                        {/* Pagination component */}
                        {(loading || total > 0 || (Array.isArray(data) && data.length > 0)) && (
                            <div className="flex justify-end mt-12">
                                <CustomPagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={loading ? 9 : (total || data?.length || 0)}
                                    pageSizeOptions={[9, 18, 27, 36, 45]}
                                    onChange={(page, limit) => {
                                        if (limit !== pageSize) {
                                            setPageSize(limit);
                                            setCurrentPage(1);
                                        } else {
                                            setCurrentPage(page);
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            ) : loading ? (
                /* Single Detail Mode Skeleton */
                <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 mt-10 flex flex-col gap-6">
                    <Skeleton.Image className="w-full !h-[350px]" active={true} style={{ width: '100%', height: '350px' }} />
                    <Skeleton.Input active block size="large" />
                    <Skeleton active paragraph={{ rows: 4 }} />
                </div>
            ) : (
                /* Single Detail Mode */
                <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 mt-8">
                    <Card className="overflow-hidden border border-gray-100 shadow-sm rounded-xl">
                        <div className="flex flex-col gap-6 md:p-4">

                            {/* Action Header / Back button */}
                            <div className="flex justify-between items-center pb-4 border-b border-gray-100">
                                <div 
                                    onClick={() => {
                                        if (window.history.state && window.history.state.idx > 0) {
                                            navigate(-1);
                                        } else {
                                            navigate('/gallery/detail');
                                        }
                                    }} 
                                    className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium text-sm"
                                >
                                    <ArrowLeftOutlined className="text-base" />
                                    <span>{t('text_back_previous_page')}</span>
                                </div>
                                <span className="text-[13px] font-semibold uppercase tracking-wider bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
                                    {(data.releaseYear ?? "")} {t('text_series')}
                                </span>
                            </div>

                            {/* Main Image */}
                            {data.picUrl && (
                                <div className="w-full h-[250px] sm:h-[380px] md:h-[480px] rounded-lg overflow-hidden relative shadow-inner">
                                    <img
                                        src={data.picUrl}
                                        alt={data.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}

                            {/* Text details */}
                            <div className="flex flex-col gap-4 mt-2">
                                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-950 leading-tight">
                                    {data.title}
                                </h1>

                                <p className="text-gray-600 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                                    {data.description}
                                </p>

                                {/* Action Button */}
                                {data.fashionId && (
                                    <div className="mt-6 p-[2px] bg-gradient-to-r from-[#50c7f8] via-[#7887fd] to-[#df97fb] rounded-md shadow-md w-full sm:w-auto self-start">
                                        <button
                                            onClick={handleShowDownloadApp}
                                            className="w-full sm:w-[260px] h-12 flex items-center justify-center bg-gradient-to-r from-[#21163e] to-[#252554] text-white hover:opacity-90 transition-all rounded-md"
                                        >
                                            <span className="text-[13px] tracking-widest text-center font-medium">
                                                {t('text_full_series')}
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </div>

                        </div>
                    </Card>
                </div>
            )}

            <FashionModals ref={appDownloadRef} />
            <PROModal open={isMemberGateOpen} onCancel={() => setIsMemberGateOpen(false)} />
        </div>
    );
}